import { defineStore } from 'pinia';
import { v4 as uuidv4 } from 'uuid';
import { computed, ref } from 'vue';
import { currentLocale } from '@/app/i18n';
import { router } from '@/app/router';
import { store } from '@/app/store';
import { analytics } from '@/services/Analytics';
import { reloadWithCacheBust } from '@/services/DeploymentVersion';
import { idleWatcher } from '@/services/IdleWatcher';
import { session } from '@/services/Session';
import { sentryService } from '@/services/telemetry/sentry';

/**
 * UI store — overlay queue (loading / error / language selector), kiosk-shell
 * UI flags, and the soft/hard restart actions.
 *
 * Migrated from `services/UI.js` (decorator-based singleton) into a Pinia
 * setup-style store. Behaviour is preserved — same overlay priority order,
 * same ERROR_FALLBACKS shape, same softRestart/hardRestart sequencing.
 *
 * `ERROR_FALLBACKS` and `OVERLAY_TYPES` are exported as plain consts (not
 * returned from the store) because consumers historically use them as
 * static lookups, not reactive state. Their callbacks read the store via
 * `useUiStore()` lazily, which works because Pinia is installed before
 * any `showError(...)` call could fire.
 */

export const OVERLAY_TYPES = {
  LOADING: 'LOADING',
  ERROR: 'ERROR',
  LANGUAGE_SELECTOR: 'LANGUAGE',
} as const;

export type OverlayType = (typeof OVERLAY_TYPES)[keyof typeof OVERLAY_TYPES];

export interface ErrorFallback {
  button: string | null;
  callback: (() => void | Promise<void>) | null;
}

export interface NormalizedError {
  error: Error;
  name: string;
  code: string | undefined;
  message: string;
  cause: unknown;
  stack: string | undefined;
  originalError: unknown;
}

export interface OverlayContext {
  error?: NormalizedError;
  fallback?: ErrorFallback;
}

export interface Overlay {
  uuid: string;
  type: OverlayType;
  singular?: boolean;
  context: OverlayContext | null;
  hide: () => void;
}

export interface CreateOverlayOptions {
  singular?: boolean;
}

export interface ShowErrorOptions {
  sentryLevel?: 'error' | 'warning' | 'info';
  // false = show the overlay + counter + analytics but do NOT create a
  // Sentry event. For expected user behavior (e.g. no face in frame) that
  // is an error *screen*, not an error *condition*.
  sentryCapture?: boolean;
}

/**
 * Maximum time (ms) a loading indicator can stay on screen before being
 * automatically removed. Prevents stuck overlays on the 24/7 kiosk when
 * a caller forgets to call .hide() or an exception bypasses cleanup.
 */
export const LOADING_INDICATOR_TIMEOUT_MS = 60_000;

/**
 * Static error-recovery fallbacks. The callbacks reference store actions
 * via `useUiStore()` so they work after Pinia is installed (which is well
 * before any error path fires).
 */
export const ERROR_FALLBACKS: Record<string, ErrorFallback> = {
  HARD_RESTART: {
    button: 'restart',
    callback: () => useUiStore().hardRestart(),
  },
  SOFT_RESTART: {
    button: 'restart',
    callback: () => useUiStore().softRestart(),
  },
  CLOSE: {
    button: 'close',
    callback: () => useUiStore().getCurrentError()?.hide(),
  },
  RETRY_CAMERA: {
    button: 'retry',
    callback: () => {
      useUiStore().getCurrentError()?.hide();
      router.redirectToStep('photo-from-webcam');
    },
  },
  RELOAD_PAGE: {
    button: 'retry',
    callback: async () => {
      try {
        await sentryService.flush(1000);
      } catch (e) {
        console.warn(
          '[UI] Sentry flush failed before reload:',
          (e as Error).message
        );
      }
      reloadWithCacheBust();
    },
  },
  NONE: {
    button: null,
    callback: null,
  },
};

function fallbackName(fallback: ErrorFallback): string {
  for (const [name, config] of Object.entries(ERROR_FALLBACKS)) {
    if (config === fallback) return name;
  }
  return 'CUSTOM';
}

export const useUiStore = defineStore('ui', () => {
  const overlayQueue = ref<Overlay[]>([]);
  const showLogoStartOverButton = ref(false);
  const showUploadedPhotos = ref(false);
  const footerProgressIndicatorCurrentStep = ref(0);
  const showPrivacyPolicyPopup = ref(false);

  /**
   * Highest-priority overlay currently in the queue.
   * Priority order: ERROR > LANGUAGE_SELECTOR > LOADING.
   *
   * Errors must take precedence over the language selector because an error
   * overlay with a retry button is the only way the user can recover. If
   * the language selector were displayed instead, the error would be hidden
   * and the user would be stuck with no way to proceed.
   */
  const currentOverlay = computed<Overlay | null>(() => {
    const priority: OverlayType[] = [
      OVERLAY_TYPES.ERROR,
      OVERLAY_TYPES.LANGUAGE_SELECTOR,
      OVERLAY_TYPES.LOADING,
    ];
    for (const t of priority) {
      const found = overlayQueue.value.find((o) => o.type === t);
      if (found) return found;
    }
    return null;
  });

  const isStepContentHidden = computed(() => Boolean(currentOverlay.value));
  const isLoading = computed(
    () => currentOverlay.value?.type === OVERLAY_TYPES.LOADING
  );
  const isError = computed(
    () => currentOverlay.value?.type === OVERLAY_TYPES.ERROR
  );
  const isLanguageSelectorOpen = computed(
    () => currentOverlay.value?.type === OVERLAY_TYPES.LANGUAGE_SELECTOR
  );
  const isIdlePopupOpen = computed(() => idleWatcher.isIdlePopupOpen);

  // Stable handler reference for idempotent subscribe/unsubscribe of the
  // STEP_CHANGED listener. Cleared on every initialize() call.
  let onStepChangedHandler: (() => void) | null = null;

  function initialize(): void {
    if (onStepChangedHandler) {
      router.off(router.EVENTS.STEP_CHANGED, onStepChangedHandler);
    }
    onStepChangedHandler = () => {
      if (overlayQueue.value.length > 0) {
        sentryService.addBreadcrumb(
          'ui.overlay',
          'Overlay queue cleared on step change',
          {
            cleared_count: overlayQueue.value.length,
            cleared_types: overlayQueue.value.map((o) => o.type),
          }
        );
      }
      overlayQueue.value = [];
    };
    router.on(router.EVENTS.STEP_CHANGED, onStepChangedHandler);
  }

  function getOverlayByType(overlayType: OverlayType): Overlay | undefined {
    return overlayQueue.value.find((overlay) => overlay.type === overlayType);
  }

  function removeOverlays(overlayType: OverlayType): void {
    const overlays = overlayQueue.value.filter((o) => o.type === overlayType);
    for (const overlay of overlays) overlay.hide();
  }

  function createOverlay(
    overlayType: OverlayType,
    options?: CreateOverlayOptions,
    context?: OverlayContext | null
  ): Overlay {
    if (options?.singular && getOverlayByType(overlayType)) {
      removeOverlays(overlayType);
    }

    const uuid = uuidv4();
    const overlay: Overlay = {
      uuid,
      type: overlayType,
      singular: options?.singular,
      context: context ?? null,
      hide: () => {
        overlayQueue.value = overlayQueue.value.filter((o) => o.uuid !== uuid);
        sentryService.addBreadcrumb(
          'ui.overlay',
          `Overlay hidden: ${overlayType}`,
          {
            overlay_type: overlayType,
            overlay_uuid: uuid.slice(0, 8),
            queue_length: overlayQueue.value.length,
          }
        );
      },
    };
    overlayQueue.value.push(overlay);

    sentryService.addBreadcrumb('ui.overlay', `Overlay shown: ${overlayType}`, {
      overlay_type: overlayType,
      overlay_uuid: uuid.slice(0, 8),
      singular: options?.singular,
      queue_length: overlayQueue.value.length,
      current_step: router.step,
    });

    return overlay;
  }

  function createLoadingIndicator(): Overlay {
    const overlay = createOverlay(OVERLAY_TYPES.LOADING);

    // Safety timeout: auto-hide loading overlays that outlive their expected
    // lifespan. On a 24/7 kiosk, a stuck loading spinner blocks all interaction.
    const safetyTimer = setTimeout(() => {
      if (overlayQueue.value.some((o) => o.uuid === overlay.uuid)) {
        console.warn('[UI] Loading indicator auto-removed after timeout');
        sentryService.addBreadcrumb(
          'ui.overlay',
          'Loading indicator auto-removed after safety timeout',
          {
            overlay_uuid: overlay.uuid.slice(0, 8),
            timeout_ms: LOADING_INDICATOR_TIMEOUT_MS,
          },
          'warning'
        );
        overlay.hide();
      }
    }, LOADING_INDICATOR_TIMEOUT_MS);

    // Wrap .hide() to also clear the safety timer
    const originalHide = overlay.hide;
    overlay.hide = () => {
      clearTimeout(safetyTimer);
      originalHide();
    };

    return overlay;
  }

  function hideAllLoadingIndicators(): void {
    removeOverlays(OVERLAY_TYPES.LOADING);
  }

  function showLanguageSelector(): void {
    sentryService.addBreadcrumb('ui.interaction', 'Language selector opened', {
      current_step: router.step,
    });
    sentryService.incrementCounter('ui.language_selector_opened', 1);
    createOverlay(OVERLAY_TYPES.LANGUAGE_SELECTOR, { singular: true });
  }

  function hideLanguageSelector(): void {
    sentryService.addBreadcrumb('ui.interaction', 'Language selector closed', {
      current_step: router.step,
    });
    removeOverlays(OVERLAY_TYPES.LANGUAGE_SELECTOR);
  }

  function normalizeError(
    code: string | undefined,
    originalError: unknown
  ): NormalizedError {
    // Extract cause: prefer .cause from Error instances, but also support
    // non-Error objects that carry a .cause property (e.g. ParsedError-like
    // objects).
    const errLike = originalError as
      | { cause?: unknown; name?: string; message?: string; stack?: string }
      | null
      | undefined;
    const cause =
      errLike?.cause !== undefined
        ? errLike.cause
        : originalError instanceof Error
          ? undefined
          : originalError;

    const name = errLike?.name || 'Error';
    const message = errLike?.message || code || 'Unknown error';
    const stack = errLike?.stack || new Error().stack;
    const error = Object.assign(new Error(message, { cause }), {
      name,
      code,
      stack,
    });

    return { error, name, code, message, cause, stack, originalError };
  }

  function showError(
    code: string | undefined,
    originalError?: unknown,
    fallback?: ErrorFallback,
    options?: ShowErrorOptions
  ): Overlay {
    const error = normalizeError(code, originalError);
    const sentryLevel = options?.sentryLevel || 'error';
    const fb = fallback || ERROR_FALLBACKS.HARD_RESTART!;
    const context: OverlayContext = { error, fallback: fb };

    console.error(
      `[${router.step || router.path}] ${error.name}: ${code}`,
      error.cause || ''
    );
    analytics.trackError(code || error.message);

    // Skip captureException if upstream already captured (e.g.
    // RestApi._captureApiError). Counter + UI overlay still happen.
    const oe = originalError as { _sentryCaptured?: boolean } | undefined;
    const innerOe = (error.error as unknown as { _sentryCaptured?: boolean })
      ?._sentryCaptured;
    const alreadyCaptured = oe?._sentryCaptured || innerOe;

    const fbName = fallbackName(fb);
    try {
      if (!alreadyCaptured && options?.sentryCapture !== false) {
        sentryService.captureException(error.error, {
          tags: {
            error_code: code,
            current_step: router.step || router.path,
            fallback_type: fbName,
          },
          extra: {
            error_name: error.name,
            error_message: error.message,
            error_cause: error.cause,
            original_error_type: (
              originalError as { constructor?: { name?: string } }
            )?.constructor?.name,
            fallback_button: fallback?.button || 'restart',
          },
          level: sentryLevel,
        });
      }

      sentryService.incrementCounter('ui.error_shown', 1, {
        error_code: code || 'unknown',
        step: router.step || 'unknown',
      });
    } catch (sentryError) {
      console.warn(
        '[UI] Sentry error tracking failed:',
        (sentryError as Error)?.message || sentryError
      );
    }

    // CRITICAL: This MUST always execute regardless of Sentry success/failure
    return createOverlay(OVERLAY_TYPES.ERROR, { singular: true }, context);
  }

  function getCurrentError(): Overlay | undefined {
    return getOverlayByType(OVERLAY_TYPES.ERROR);
  }

  async function softRestart(): Promise<void> {
    sentryService.addBreadcrumb(
      'ui.restart',
      'Soft restart initiated',
      {
        current_step: router.step,
        // Full session ID is an internal Postgres join key; keep it intact
        // for restart/debug telemetry.
        session_id: session.id as string | null,
      },
      'warning'
    );
    sentryService.incrementCounter('ui.soft_restart', 1, {
      step: router.step || 'unknown',
    });

    const loadingIndicator = createLoadingIndicator();
    try {
      try {
        await session.end('abandoned');
      } catch (error) {
        console.warn('[UI] Session end failed during soft restart:', error);
      }
      // Teardown resets i18n, but the URL still contains the visitor's locale.
      await router.redirectToStep('', {
        omitQuery: true,
        localeCode: currentLocale.value?.code,
      });
    } finally {
      loadingIndicator.hide();
    }
  }

  async function hardRestart(): Promise<void> {
    sentryService.addBreadcrumb(
      'ui.restart',
      'Hard restart initiated',
      {
        current_step: router.step,
        session_id: session.id as string | null,
        kiosk_slug: store.kiosk?.slug,
      },
      'warning'
    );
    sentryService.incrementCounter('ui.hard_restart', 1, {
      step: router.step || 'unknown',
    });

    analytics.trackBoolean(analytics.METRICS.session_manual_reset, {});

    // End session first so in-flight ops don't fail with missing session ID.
    try {
      await session.end('abandoned');
    } catch (e) {
      console.warn(
        '[UI] Session end failed during hard restart:',
        (e as Error).message
      );
    }

    // Flush Sentry before reload. analytics.flush() isn't needed: session.end
    // calls analytics.flushForSession() with the correct (ending) session ID.
    try {
      await sentryService.flush(2000);
    } catch (e) {
      console.warn(
        '[UI] Sentry flush failed during hard restart:',
        (e as Error)?.message
      );
    }

    restoreInitialKioskUrlIfNeeded();
    reloadWithCacheBust();
  }

  function restoreInitialKioskUrlIfNeeded(): void {
    if (router.isStepPage && store.kiosk?.slug) {
      let normalizedUrl = `/${store.kiosk.slug}`;
      if (router.localeCode) {
        normalizedUrl += `/${router.localeCode}`;
      }
      if (store.kioskId) {
        normalizedUrl += `?kiosk_id=${store.kioskId}`;
      }
      history.replaceState({}, '', normalizedUrl);
    }
  }

  return {
    // State
    overlayQueue,
    showLogoStartOverButton,
    showUploadedPhotos,
    footerProgressIndicatorCurrentStep,
    showPrivacyPolicyPopup,
    // Computed
    currentOverlay,
    isStepContentHidden,
    isLoading,
    isError,
    isLanguageSelectorOpen,
    isIdlePopupOpen,
    // Actions
    initialize,
    createOverlay,
    getOverlayByType,
    removeOverlays,
    createLoadingIndicator,
    hideAllLoadingIndicators,
    showLanguageSelector,
    hideLanguageSelector,
    showError,
    normalizeError,
    getCurrentError,
    softRestart,
    hardRestart,
    restoreInitialKioskUrlIfNeeded,
  };
});
